import IconPencil from "assets/img/icon-pencil.svg?react"
import IconReset from "assets/img/icon-reset.svg?react"
import IconCopy from "assets/img/icon-copy.svg?react"
import pictogramAsset from "assets/img/pictogram/asset.svg"

import React, { FunctionComponent, useRef, useMemo, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { uniq } from "lodash"

import { Grid, GridItem } from "components/Grid"
import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import Modal from "components/Modal/Modal"
import { Guid } from "@/types"
import Button from "components/Button/Button"
import { ArticleType, UserPermission } from "config/constants"
import Routes from "@/routes"
import useAsset from "hooks/useAsset"
import useArticle from "hooks/useArticle"
import Pictogram from "components/Pictogram/Pictogram"
import InfoButton from "components/InfoButton/InfoButton"
import articleSidebars from "stores/articleSidebars"
import Lmid from "components/Lmid/Lmid"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import AssetInvoicesIncoming from "components/AssetInvoicesIncoming/AssetInvoicesIncoming"
import AssetInvoicesOutgoing from "components/AssetInvoicesOutgoing/AssetInvoicesOutgoing"
import Tabs from "components/Tabs/Tabs"
import TabsItem from "components/Tabs/TabsItem/TabsItem"
import AssetCollection from "components/ConnectedAssets/AssetCollection"
import StatusLight, { StatusLightType } from "components/StatusLight/StatusLight"
import AssetVersionsControl from "./AssetVersionsControl"
import Status, { StatusType } from "components/Status/Status"
import { usePermissions } from "stores/permissions"
import { ArticleZts } from "models/ArticleZts"
import { getArticleDescriptionHeaderTextType } from "utils/utils"
import AssetAlternativeArticleDescriptionModal from "./AssetAlternativeArticleDescriptionModal"
import AssetService from "services/AssetService"
import AssetLabels from "components/AssetLabels/AssetLabels"
import useUserEmails from "hooks/useUserEmails"
import OperationHoursRecord, { ICreateOperationHoursRecord } from "models/OperationHoursRecord"
import toastMessages from "stores/toastMessages"
import RecordedOperationHoursModal from "components/RecordedOperationHoursModal/RecordedOperationHoursModal"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import AssetSidebarAssetData from "./AssetSidebarAssetData"
import NoValue from "components/NoValue/NoValue"
import useAssetDisplayStatuses from "hooks/useAssetDisplayStatuses"
import AssetStoredStatus from "components/AssetStoredStatus/AssetStoredStatus"
import { AssetStoredStatus as AssetStoredStatusEnum } from "models/AssetDisplayStatus"
import useReservation from "hooks/useReservation"
import AssetPicturesWithVersions from "./AssetPicturesWithVersions"
import AssetDocumentsWithVersions from "./AssetDocumentsWithVersions"
import AssetMaintenancesWithVersions from "./AssetMaintenancesWithVersions"
import ReservationsWithVersions from "./ReservationsWithVersions"
import ReactivateAssetModal from "components/ReactivateAssetModal/ReactivateAssetModal"
import CopyAssetFlow from "components/CopyAsset/CopyAssetFlow"
import canCopyAsset from "components/CopyAsset/canCopyAsset"


export interface IAssetSidebarProps {
    assetId: Guid
    initTabId?: AssetSidebarTab
    initTabSubIndex?: AssetSidebarTabAssetData | null
    onClose?(): void
    onEditClick?(): void
    onReservationDelete?(): void
}

export enum AssetSidebarTab {
    assetData = 'assetData',
    requests = 'requests',
    maintenance = 'maintenance',
    attachments = 'attachments',
    invoices = 'invoices',
}

export enum AssetSidebarTabAssetData {
    assetData = 0,
    connectedAssets,
    labels,
}

const AssetSidebar: FunctionComponent<React.PropsWithChildren<IAssetSidebarProps>> = ({
    assetId: assetIdProp,
    initTabId = AssetSidebarTab.assetData,
    initTabSubIndex = AssetSidebarTabAssetData.assetData,
    onClose,
    onEditClick,
    onReservationDelete
}) => {
    const { t } = useTranslation()
    const location = useLocation()
    const [assetId, setAssetId] = useState(assetIdProp)
    const { asset, refetchAsset } = useAsset(assetId, onClose)
    const { article } = useArticle(asset?.articleId, onClose)
    const [hasLabelsTabBeenActivated, setHasLabelsTabBeenActivated] = useState(false)
    const [hasRequestsTabBeenActivated, setHasRequestsTabBeenActivated] = useState(false)
    const [hasMaintenanceTabBeenActivated, setHasMaintenanceTabBeenActivated] = useState(false)
    const [showAlternativeDescriptionModal, setShowAlternativeDescriptionModal] = useState(false)
    const scrollEl = useRef<HTMLDivElement>(null)
    const { hasPermission } = usePermissions();
    const outgoingInvoicesPermission = hasPermission(UserPermission.OutgoingInvoicesView)
    const incomingInvoicesPermission = hasPermission(UserPermission.IncomingInvoicesView)
    const assetMaintenanceViewPermission = hasPermission(UserPermission.AssetMaintenanceView)
    const canReactivateAssets = hasPermission(UserPermission.ReactivateDisposedAssets)
    const [activeTab, setActiveTab] = useState({ tabId: initTabId, subIndex: initTabSubIndex })
    const [showReactivateModal, setShowReactivateModal] = useState(false)
    const [showCopyAssetFlow, setShowCopyAssetFlow] = useState(false)

    const { loading: displayStatusLoading, displayStatuses } = useAssetDisplayStatuses(assetId)
    const displayStatus = displayStatuses?.[assetId]

    const { reservation } = useReservation({ reservationId: displayStatus?.reservationId })
    const eventAddress = reservation?.address

    const assetCreateChangeUserIds = useMemo(() => {
        if (!asset) return []
        return uniq([
            asset.createdBy,
            asset.changedBy,
        ]).filter(id => id) as Guid[]
    }, [asset])
    const { getEmail } = useUserEmails({ userIds: assetCreateChangeUserIds })
    const createdByEmail = getEmail(asset?.createdBy)
    const changedByEmail = getEmail(asset?.changedBy)

    function handleEditClick(e: MouseEvent) {
        if (typeof onEditClick !== "function") return
        e.stopPropagation()
        e.preventDefault()
        onEditClick()
    }

    function handleReservationDelete() {
        if (typeof onReservationDelete === "function") {
            onReservationDelete()
        }
    }

    const handleTabSwitch = (index?: number, subIndex?: number | null, id?: string) => {
        setActiveTab({ tabId: id as AssetSidebarTab, subIndex: subIndex ?? null })
        if (id === AssetSidebarTab.requests) {
            setHasRequestsTabBeenActivated(true)
        } else if (id === AssetSidebarTab.maintenance) {
            setHasMaintenanceTabBeenActivated(true)
        } else if (id === AssetSidebarTab.assetData && subIndex === AssetSidebarTabAssetData.labels) {
            setHasLabelsTabBeenActivated(true)
        }
    }

    useEffect(() => {
        if (initTabId) {
            handleTabSwitch(undefined, initTabSubIndex, initTabId)
        }
    }, [initTabId, initTabSubIndex])

    async function updateAlternativeArticleDescription(newAlternativeArticleDescription: string) {
        if (!asset || !asset.linkUpdateAltArticleDescription) {
            throw new Error('Asset or linkUpdateAltArticleDescription is missing')
        }

        await AssetService.updateAssetAltArticleDescription(asset.linkUpdateAltArticleDescription, newAlternativeArticleDescription)
        asset.altArticleDescription = newAlternativeArticleDescription
    }

    const [recordedOperationHours, setRecordedOperationHours] = useState<OperationHoursRecord[] | null>(null)
    const [showRecordedOperationHoursModal, setShowRecordedOperationHoursModal] = useState(false)
    const [shouldRefetchAssetOnOperationHoursModalClose, setShouldRefetchAssetOnOperationHoursModalClose] = useState(false)

    async function fetchOperationHoursRecords() {
        if (!asset?.linkGetOperationHours) return
        try {
            const resp = await AssetService.getAssetOprationHoursRecords(asset.linkGetOperationHours)
            setRecordedOperationHours(resp)
        } catch (e) {
            toastMessages.showSystemError()
            setShowRecordedOperationHoursModal(false)
        }
    }

    async function handleOperatingHoursLogClick() {
        setShowRecordedOperationHoursModal(true)
        if (recordedOperationHours) return
        fetchOperationHoursRecords()
    }

    async function handleOperationHoursModalClose() {
        if (shouldRefetchAssetOnOperationHoursModalClose) {
            refetchAsset()
            setShouldRefetchAssetOnOperationHoursModalClose(false)
        }
        setShowRecordedOperationHoursModal(false)
    }

    async function handleOperationHoursRecordAdd(data: ICreateOperationHoursRecord) {
        if (!asset?.linkAddOperationHours) {
            console.error("No link to add operation hours")
            toastMessages.showSystemError()
            return
        }
        try {
            await AssetService.addAssetOprationHoursRecord(asset.linkAddOperationHours, data)
            await fetchOperationHoursRecords()
            setShouldRefetchAssetOnOperationHoursModalClose(true)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }

    const operationHoursLogButton = asset?.linkGetOperationHours
        ? <Button
            theme="link"
            label={t('asset.showLog')}
            onClick={handleOperatingHoursLogClick}
        />
        : null

    const canReactivate = canReactivateAssets && !!asset?.disposal && !!asset?.isCurrentVersion
    const canCopy = canCopyAsset(asset, article, hasPermission)

    return (
        <>
            <Modal type="sidebar"
                isBusy={!asset || !article}
                title={t('asset.assetDetails')}
                onClose={onClose}
                closeButton={!asset?.linkUpdate}
                modalBodyContentEl={scrollEl}
                maxWidth="787px"
                toolbar={
                    <Grid columns="auto max-content" gap="2.4rem" className="w-100">
                        <GridItem alignSelf="center" justifySelf="start">
                            {!!asset && !asset?.isOperational &&
                                <StatusLight type={StatusLightType.warning}>
                                    {t('asset.assetCurrentlyOutOfOrder')}
                                </StatusLight>
                            }
                        </GridItem>
                        <GridItem alignSelf="center">
                            <Flex alignItems="center" gap="0.8rem">
                                {canReactivate &&
                                    <Button
                                        round
                                        theme="grey"
                                        ariaLabel={t("assetReactivation.reactivate")}
                                        icon={<IconReset />}
                                        onClick={() => setShowReactivateModal(true)}
                                    />
                                }
                                {canCopy &&
                                    <Button
                                        round
                                        theme="grey"
                                        ariaLabel={t("copyAsset.copyAsset")}
                                        title={t("copyAsset.copyAsset")}
                                        icon={<IconCopy />}
                                        onClick={() => setShowCopyAssetFlow(true)}
                                    />
                                }
                                {!!asset?.linkUpdate &&
                                    <Button
                                        type="link"
                                        url={Routes.assetEdit.getPath({ assetId: asset.id! }, { backUrl: location.pathname + location.search })}
                                        className="ml-2"
                                        round
                                        theme="grey"
                                        ariaLabel={t('common.edit')}
                                        icon={<IconPencil />}
                                        onClick={handleEditClick}>
                                    </Button>
                                }
                            </Flex>
                        </GridItem>
                    </Grid>
                }>
                {!!asset && !!article &&
                    <>
                        <Grid
                            className="mb-4"
                            columns="6.4rem auto"
                            gap="2.4rem">
                            <GridItem>
                                <Pictogram
                                    src={pictogramAsset} />
                            </GridItem>
                            <GridItem>
                                <Flex
                                    alignItems="center">
                                    <Text type={getArticleDescriptionHeaderTextType(asset.altArticleDescription || article.description)} className="mr-2">
                                        <ArticleDescription
                                            articleDescription={asset.altArticleDescription || article.description}
                                            clamp={2}
                                        />
                                    </Text>
                                    <InfoButton
                                        onClick={() => articleSidebars.show({ articleId: article.id! })}
                                    />
                                    {!!asset.linkUpdateAltArticleDescription &&
                                        <Button
                                            onClick={() => setShowAlternativeDescriptionModal(true)}
                                            icon={<IconPencil />}
                                            theme="transparent"
                                            round
                                        />
                                    }
                                </Flex>
                                {asset.altArticleDescription
                                    ? <div className="color-grey-500 mb-1">
                                        {t('alternativeArticleDescription.alternativeArticleDescription')}
                                    </div>
                                    : (
                                        article instanceof ArticleZts && article.descriptionOverwritten
                                            ? <div className="color-grey-500 mb-1">
                                                {t('article.notOriginalZtsDescription')}
                                            </div>
                                            : null
                                    )
                                }
                                <Grid columns="max-content minmax(0, max-content) max-content max-content" gap="0.8rem">
                                    <GridItem>{t('common.lmid')}:</GridItem>
                                    <GridItem>
                                        <Lmid
                                            lmid={asset.lmid}
                                            labeled={asset.isLabeled}
                                            inCollection={asset.linkedToAssetCollection}
                                            copy
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <AssetStatusTag status={asset.status} />
                                    </GridItem>
                                    <GridItem>
                                        {displayStatusLoading
                                            ? <NoValue>{t("common.loading")}</NoValue>
                                            : (!!displayStatus?.status && displayStatus.status !== AssetStoredStatusEnum.notApplicable
                                                ? <AssetStoredStatus status={displayStatus.status} />
                                                : null
                                            )
                                        }
                                    </GridItem>
                                </Grid>
                                {!!asset.splitParentId && !!asset.splitParentLmid && asset.splitParentLmid !== asset.lmid &&
                                    <Lmid
                                        lmid={asset.splitParentLmid}
                                        assetId={asset.splitParentId}
                                        isSplit
                                    />}
                                {asset.hasMultipleVersions &&
                                    <AssetVersionsControl
                                        isCurrentVersion={!!asset.isCurrentVersion}
                                        version={asset.version}
                                        startDate={asset.availabilityDate}
                                        endDate={asset.disposal?.date}
                                        linkGetAssetVersions={asset.linkGetAssetVersions}
                                        onSwitchToVersion={setAssetId}
                                    />
                                }
                            </GridItem>
                        </Grid>
                        {!asset.isCurrentVersion &&
                            <div className="mb-4">
                                <Status type={StatusType.error}>
                                    {t('asset.thisIsPerviousVersion')}
                                </Status>
                            </div>
                        }
                        <Tabs onSwitchTab={handleTabSwitch}
                            defaultActiveId={initTabId}
                            defaultActiveSubIndex={initTabSubIndex}>
                            <TabsItem
                                id={AssetSidebarTab.assetData}
                                label={t('asset.assetData')}
                                subItemsAlign="left"
                                subItemsWidth="22rem">
                                <TabsItem label={t('asset.assetData')}>
                                    <div className="mb-8">
                                        <AssetSidebarAssetData
                                            asset={asset}
                                            article={article}
                                            displayStatus={displayStatus}
                                            displayStatusLoading={displayStatusLoading}
                                            operationHoursLogButton={operationHoursLogButton}
                                            eventAddress={eventAddress}
                                        />
                                    </div>
                                </TabsItem>
                                <TabsItem label={t('asset.manageConnectedAssets')}>
                                    <AssetCollection
                                        assetId={asset.id!}
                                        linkGetAssetCollection={asset.linkGetAssetCollection}
                                        linkUpdateAssetCollection={asset.linkUpdateAssetCollection}
                                        onCollectionChanged={hasConnectedAssets => asset.linkedToAssetCollection = hasConnectedAssets}
                                    />
                                </TabsItem>
                                {!!asset.linkGetAssetLabelGroups &&
                                    <TabsItem label={t('asset.manageLabels')}>
                                        {hasLabelsTabBeenActivated &&
                                            <AssetLabels
                                                linkGetAssetLabelGroups={asset.linkGetAssetLabelGroups}
                                            />
                                        }
                                    </TabsItem>
                                }
                            </TabsItem>
                            <TabsItem
                                id={AssetSidebarTab.requests}
                                label={t('asset.requests')}>
                                {hasRequestsTabBeenActivated &&
                                    <ReservationsWithVersions
                                        asset={asset}
                                        isMachine={article.articleType === ArticleType.machine}
                                        onDeleteReservation={handleReservationDelete}
                                        linkGetAssetVersions={asset.linkGetAssetVersions}
                                        currentAssetVersion={asset.version}
                                        isCurrentVersion={asset.isCurrentVersion}
                                    />
                                }
                            </TabsItem>
                            {assetMaintenanceViewPermission &&
                                <TabsItem
                                    id={AssetSidebarTab.maintenance}
                                    label={t('maintenance.maintenance')}>
                                    <>
                                        {hasMaintenanceTabBeenActivated &&
                                            <AssetMaintenancesWithVersions
                                                assetId={asset.id!}
                                                assetOperationHours={asset.operationHours}
                                                linkCreateAssetMaintenance={asset.linkCreateAssetMaintenance}
                                                linkGetAssetVersions={asset.linkGetAssetVersions}
                                                currentAssetVersion={asset.version}
                                                isCurrentVersion={asset.isCurrentVersion}
                                            />
                                        }
                                    </>
                                </TabsItem>
                            }
                            <TabsItem
                                id={AssetSidebarTab.attachments}
                                label={t('common.attachments')}
                                subItemsAlign="left"
                                subItemsWidth="22rem">
                                <TabsItem label={t('common.pictures')}>
                                    <AssetPicturesWithVersions
                                        assetId={asset.id!}
                                        lmid={asset.lmid}
                                        scrollEl={scrollEl}
                                        linkGetAssetVersions={asset.linkGetAssetVersions}
                                        currentAssetVersion={asset.version}
                                        isCurrentVersion={asset.isCurrentVersion}
                                    />
                                </TabsItem>
                                <TabsItem label={t('common.documents')}>
                                    <AssetDocumentsWithVersions
                                        lmid={asset.lmid}
                                        linkGetAssetDocuments={asset.linkGetAssetDocuments}
                                        linkAddAssetDocuments={asset.linkAddAssetDocuments}
                                        linkGetAssetVersions={asset.linkGetAssetVersions}
                                        currentAssetVersion={asset.version}
                                        isCurrentVersion={asset.isCurrentVersion}
                                    />
                                </TabsItem>
                            </TabsItem>
                            {(outgoingInvoicesPermission || incomingInvoicesPermission) &&
                                <TabsItem
                                    id={AssetSidebarTab.invoices}
                                    label={t('asset.invoices')}
                                    subItemsAlign="right"
                                    subItemsWidth="22rem">
                                    {[
                                        outgoingInvoicesPermission && <TabsItem key="outgoing" label={t('asset.outgoingInvoices')}>
                                            <AssetInvoicesOutgoing
                                                assetId={asset.id!}
                                                scrollEl={scrollEl}
                                            />
                                        </TabsItem>,
                                        incomingInvoicesPermission && <TabsItem key="incoming" label={t('asset.incomingInvoices')}>
                                            <AssetInvoicesIncoming
                                                assetId={asset.id!}
                                                scrollEl={scrollEl}
                                            />
                                        </TabsItem>].filter(el => !!el)}
                                </TabsItem>
                            }
                        </Tabs>
                        {activeTab.tabId === AssetSidebarTab.assetData && activeTab.subIndex === AssetSidebarTabAssetData.assetData &&
                            <FlexItem className="text-right color-grey-500 mt-auto">
                                {!!asset.created &&
                                    <Text type="pSmallest" className="font-bold">
                                        {t(createdByEmail ? "common.createdByOnX" : "common.createdOnX", {
                                            user: createdByEmail,
                                            date: asset.created.format(t("config.dateLongTimeFormat"))
                                        })}
                                    </Text>
                                }
                                {!!asset.changed && (changedByEmail !== createdByEmail || !asset.changed.isSame(asset.created)) &&
                                    <Text type="pSmallest">
                                        {t(createdByEmail ? "common.lastChangedByOnX" : "common.lastChangedOnX", {
                                            user: changedByEmail,
                                            date: asset.changed.format(t("config.dateLongTimeFormat"))
                                        })}
                                    </Text>
                                }
                            </FlexItem>
                        }
                    </>
                }
            </Modal>
            {!!asset && !!article && showAlternativeDescriptionModal &&
                <AssetAlternativeArticleDescriptionModal
                    asset={asset}
                    article={article}
                    onClose={() => setShowAlternativeDescriptionModal(false)}
                    onSave={updateAlternativeArticleDescription}
                />
            }
            {showRecordedOperationHoursModal &&
                <RecordedOperationHoursModal
                    recordedOperationHours={recordedOperationHours}
                    onClose={handleOperationHoursModalClose}
                    onAdd={asset?.linkAddOperationHours ? handleOperationHoursRecordAdd : undefined}
                />
            }
            {!!asset && showReactivateModal &&
                <ReactivateAssetModal
                    assetId={asset.id!}
                    lmid={asset.lmid}
                    articleDescription={asset.altArticleDescription || article?.description}
                    relatedRequestNumber={asset.availabilityReservationNumber || undefined}
                    relatedRequestId={asset.availabilityReservationId || undefined}
                    onClose={() => setShowReactivateModal(false)}
                    onSuccess={() => onClose?.()}
                />
            }
            {!!asset && !!article && showCopyAssetFlow &&
                <CopyAssetFlow
                    asset={asset}
                    article={article}
                    storedStatus={displayStatus?.status}
                    onClose={() => setShowCopyAssetFlow(false)}
                />
            }
        </>
    )
}

export default AssetSidebar
